import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Container,
	Flex,
	Heading,
	HStack,
	Icon,
	SimpleGrid,
	Text,
	VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiMessageCircle, FiUser } from 'react-icons/fi';
import AiGuideRobot from '../../../components/Reusables/GuidedWalkthrough/AiGuideRobot';
import FilmGrain from './FilmGrain';
import { FAQ_ITEMS } from './homePageContent';

const MotionBox = motion.create(Box);

const blink = keyframes`
  0%, 45% { opacity: 1; }
  46%, 100% { opacity: 0; }
`;

const Cursor = () => (
	<Box
		as='span'
		display='inline-block'
		w='2px'
		h='0.95em'
		bg='cyan.300'
		ml='1px'
		verticalAlign='text-bottom'
		animation={`${blink} 0.9s step-end infinite`}
		aria-hidden
	/>
);

const useTypewriter = (text, speed = 14, active = true) => {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		setIndex(0);
	}, [text]);

	useEffect(() => {
		if (!active || index >= text.length) return undefined;
		const timer = window.setTimeout(() => setIndex((i) => i + 1), speed);
		return () => window.clearTimeout(timer);
	}, [active, index, text, speed]);

	return { displayed: text.slice(0, index), isComplete: index >= text.length };
};

const UserBubble = ({ children }) => (
	<Flex justify='flex-end' mb={4}>
		<Box
			maxW='90%'
			px={4}
			py={3}
			rounded='2xl'
			roundedTopRight='sm'
			bg='whiteAlpha.200'
			border='1px solid'
			borderColor='whiteAlpha.250'>
			<HStack spacing={2} mb={2} justify='flex-end'>
				<Text fontSize='xs' fontWeight='bold' color='whiteAlpha.700' mb={0}>
					You
				</Text>
				<Flex
					w={6}
					h={6}
					rounded='full'
					bg='whiteAlpha.300'
					align='center'
					justify='center'>
					<Icon as={FiUser} color='white' boxSize={3} />
				</Flex>
			</HStack>
			<Text color='white' fontSize='sm' fontWeight='medium' mb={0} lineHeight='tall'>
				{children}
			</Text>
		</Box>
	</Flex>
);

const BlogsmithBubble = ({ children, isTyping }) => (
	<Flex justify='flex-start' align='flex-start' gap={3}>
		<Flex
			w={10}
			h={10}
			rounded='xl'
			bg='cyan.400'
			align='center'
			justify='center'
			flexShrink={0}
			overflow='hidden'
			mt={1}>
			<AiGuideRobot size={44} />
		</Flex>
		<Box
			flex={1}
			maxW='92%'
			px={4}
			py={3}
			rounded='2xl'
			roundedTopLeft='sm'
			bg='blackAlpha.350'
			border='1px solid'
			borderColor='cyan.400'>
			<HStack spacing={2} mb={2}>
				<Text fontSize='xs' fontWeight='bold' color='cyan.300' mb={0}>
					Blogsmith
				</Text>
				{isTyping && (
					<Text fontSize='10px' color='whiteAlpha.500' mb={0} fontFamily='mono'>
						typing…
					</Text>
				)}
			</HStack>
			<Text color='whiteAlpha.900' fontSize='sm' mb={0} lineHeight='tall'>
				{children}
			</Text>
		</Box>
	</Flex>
);

export default function HomePageBlogsmithFaq() {
	const [activeId, setActiveId] = useState(FAQ_ITEMS[0].id);
	const activeItem = FAQ_ITEMS.find((item) => item.id === activeId) || FAQ_ITEMS[0];
	const answer = useTypewriter(activeItem.blogsmithReply, 12, true);

	return (
		<Box
			id='faq'
			py={{ base: 16, md: 22 }}
			bg='linear-gradient(160deg, #0f172a 0%, #0d2847 45%, #134e4a 100%)'
			position='relative'
			overflow='hidden'>
			<FilmGrain opacity={0.03} />
			<Box
				position='absolute'
				top='-15%'
				right='-8%'
				w='420px'
				h='420px'
				bg='cyan.400'
				filter='blur(100px)'
				opacity={0.12}
				borderRadius='full'
				pointerEvents='none'
				aria-hidden
			/>

			<Container maxW='container.xl' position='relative' zIndex={1} py={{ base: 10, md: 16 }}>
				<VStack spacing={3} textAlign='center' mb={10}>
					<HStack
						spacing={2}
						bg='whiteAlpha.100'
						px={4}
						py={1.5}
						rounded='full'
						border='1px solid'
						borderColor='whiteAlpha.200'>
						<Icon as={FiMessageCircle} color='cyan.300' />
						<Text fontSize='sm' color='cyan.200' fontWeight='semibold' mb={0}>
							Ask Blogsmith
						</Text>
					</HStack>
					<Heading size='2xl' color='white' fontWeight='extrabold'>
						Questions before you pilot?
					</Heading>
					<Text color='whiteAlpha.700' fontSize='lg' maxW='xl' mb={0}>
						Pick a question — Blogsmith will answer in plain language.
					</Text>
				</VStack>

				<SimpleGrid columns={{ base: 1, lg: 12 }} spacing={{ base: 6, lg: 8 }} alignItems='stretch'>
					<VStack align='stretch' spacing={2} gridColumn={{ lg: 'span 5' }}>
						{FAQ_ITEMS.map((item) => {
							const isActive = item.id === activeId;
							return (
								<Button
									key={item.id}
									variant='unstyled'
									textAlign='left'
									w='full'
									h='auto'
									px={4}
									py={3.5}
									rounded='xl'
									bg={isActive ? 'whiteAlpha.200' : 'whiteAlpha.50'}
									border='2px solid'
									borderColor={isActive ? 'cyan.400' : 'whiteAlpha.100'}
									boxShadow={isActive ? '0 0 24px rgba(34,211,238,0.15)' : 'none'}
									_hover={{
										bg: 'whiteAlpha.150',
										borderColor: isActive ? 'cyan.400' : 'whiteAlpha.300',
										transform: 'translateX(4px)',
									}}
									transition='all 0.25s cubic-bezier(0.22, 1, 0.36, 1)'
									onClick={() => setActiveId(item.id)}
									aria-pressed={isActive}>
									<Text
										fontSize='sm'
										fontWeight={isActive ? 'bold' : 'semibold'}
										color={isActive ? 'white' : 'whiteAlpha.800'}
										mb={0}
										lineHeight='snug'>
										{item.q}
									</Text>
								</Button>
							);
						})}
					</VStack>

					<Box
						gridColumn={{ lg: 'span 7' }}
						bg='blackAlpha.400'
						border='1px solid'
						borderColor='whiteAlpha.200'
						rounded='2xl'
						p={{ base: 4, md: 6 }}
						backdropFilter='blur(16px)'
						minH={{ base: '320px', md: '380px' }}
						display='flex'
						flexDirection='column'>
						<HStack spacing={3} pb={4} mb={4} borderBottom='1px solid' borderColor='whiteAlpha.150'>
							<Flex
								w={9}
								h={9}
								rounded='lg'
								bg='cyan.400'
								align='center'
								justify='center'
								overflow='hidden'>
								<AiGuideRobot size={40} />
							</Flex>
							<Box>
								<Text fontWeight='bold' color='white' fontSize='sm' mb={0}>
									Blogsmith
								</Text>
								<Text fontSize='xs' color='whiteAlpha.600' mb={0} fontFamily='mono'>
									Ops Pilot assistant · online
								</Text>
							</Box>
							<Box
								ml='auto'
								w={2}
								h={2}
								rounded='full'
								bg='green.400'
								boxShadow='0 0 8px #48BB78'
							/>
						</HStack>

						<Box flex={1} role='log' aria-live='polite' aria-relevant='additions text'>
							<AnimatePresence mode='wait'>
								<MotionBox
									key={activeId}
									initial={{ opacity: 0, y: 12 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -8 }}
									transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
									<UserBubble>{activeItem.q}</UserBubble>
									<BlogsmithBubble isTyping={!answer.isComplete}>
										{answer.displayed}
										{!answer.isComplete && <Cursor />}
									</BlogsmithBubble>
								</MotionBox>
							</AnimatePresence>
						</Box>

						<Text fontSize='xs' color='whiteAlpha.500' mt={4} mb={0} fontFamily='mono'>
							AI-guided answers · {FAQ_ITEMS.length} topics
						</Text>
					</Box>
				</SimpleGrid>
			</Container>
		</Box>
	);
}
